import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  bulkCreateSubstitutions,
  createSubstitution,
  deleteSubstitution,
  exportSubstitutionsXlsx,
  fetchSubstitutionEmployee,
  fetchSubstitutionEmployees,
  getApiErrorMessage,
  updateSubstitution,
} from '../api';
import Loading from '../components/Loading';
import AbsenceTile from '../components/vacations/AbsenceTile';
import Avatar from '../components/vacations/Avatar';
import ClientTeam from '../components/vacations/ClientTeam';
import CoverageBar from '../components/vacations/CoverageBar';
import EmployeeCard from '../components/vacations/EmployeeCard';
import SubstitutionChip from '../components/vacations/SubstitutionChip';
import SubstitutionFormRow, { SubstituteSelect } from '../components/vacations/SubstitutionFormRow';
import SubstitutionsTableSkeleton from '../components/vacations/SubstitutionsTableSkeleton';
import Switch from '../components/vacations/Switch';
import '../vacationSubstitutions.css';
import { ROLE_LABELS } from '../constants/staffRoles';
import { formatRange, formatShortDate, moscowToday } from './vacationDates';

export { formatShortDate, formatRange };

export function datesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart <= bEnd && aEnd >= bStart;
}

export function coverageClass(covered, total) {
  if (!total) return null;
  if (covered <= 0) return 'vs-badge--none';
  if (covered < total) return 'vs-badge--partial';
  return 'vs-badge--full';
}

const MS_PER_DAY = 86400000;

function utcDay(iso) {
  const [year, month, day] = iso.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

export function rowCoverage(substitutions, start, end) {
  if (!start || !end || start > end) return null;
  const startUtc = utcDay(start);
  const endUtc = utcDay(end);
  const total = (endUtc - startUtc) / MS_PER_DAY + 1;
  let covered = 0;
  for (let ms = startUtc; ms <= endUtc; ms += MS_PER_DAY) {
    const d = new Date(ms).toISOString().slice(0, 10);
    if ((substitutions || []).some((s) => s.start_date <= d && d <= s.end_date)) {
      covered += 1;
    }
  }
  if (covered === 0) return 'none';
  if (covered === total) return 'full';
  return 'partial';
}

export function countHiddenPast(rows) {
  return (rows || []).reduce(
    (n, row) => n + (row.substitutions || []).filter((s) => s.status === 'past').length,
    0
  );
}

export function coverageSummary(rows, start, end) {
  if (rowCoverage([], start, end) === null) return null;
  return {
    full: (rows || []).filter((r) => rowCoverage(r.substitutions, start, end) === 'full').length,
    total: (rows || []).length,
  };
}

function nextAbsence(absences, today) {
  return (absences || [])
    .filter((a) => a.end_date >= today)
    .sort((a, b) => a.start_date.localeCompare(b.start_date))[0] || null;
}

export function splitEmployees(employees, today) {
  const upcoming = [];
  const rest = [];
  (employees || []).forEach((emp) => {
    const next = nextAbsence(emp.absences, today);
    if (next) upcoming.push({ ...emp, nextAbsence: next });
    else rest.push(emp);
  });
  upcoming.sort(
    (a, b) =>
      a.nextAbsence.start_date.localeCompare(b.nextAbsence.start_date) ||
      a.full_name.localeCompare(b.full_name, 'ru')
  );
  rest.sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru'));
  return { upcoming, rest };
}

export function exportFilename(year, fullName) {
  const first = String(fullName ?? '').trim().split(/\s+/)[0] || '';
  return `замены_${year}_${first}.xlsx`;
}

export function substituteWarning(employees, substituteId, start, end) {
  if (!substituteId || !start || !end) return null;
  const emp = (employees || []).find((e) => e.id === Number(substituteId));
  if (!emp) return null;
  const hit = (emp.absences || []).find((a) => datesOverlap(a.start_date, a.end_date, start, end));
  if (!hit) return null;
  return `${emp.full_name} тоже отсутствует ${formatRange(hit.start_date, hit.end_date)}`;
}

function absenceTileState(absence, today) {
  if (absence.end_date < today) return 'past';
  if (absence.start_date <= today && today <= absence.end_date) return 'current';
  return 'upcoming';
}

export default function VacationSubstitutions({ year }) {
  const today = moscowToday();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get('employee')) || null;

  const [employees, setEmployees] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [presetDates, setPresetDates] = useState({ start: '', end: '' });
  const [presetVersion, setPresetVersion] = useState(0);
  const [bulkSubstitute, setBulkSubstitute] = useState('');
  const [bulkComment, setBulkComment] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [openForm, setOpenForm] = useState(null); // { rowKey, substitutionId|null }
  const [formError, setFormError] = useState(null);
  const [formBusy, setFormBusy] = useState(false);
  const [panelError, setPanelError] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [showPastAbsences, setShowPastAbsences] = useState(false);
  const [query, setQuery] = useState('');
  const detailRequestId = useRef(0);

  const loadList = useCallback(() => {
    setLoadingList(true);
    return fetchSubstitutionEmployees(year)
      .then((data) => { setEmployees(Array.isArray(data) ? data : []); setListError(null); })
      .catch((err) => setListError(getApiErrorMessage(err, 'Не удалось загрузить бухгалтеров')))
      .finally(() => setLoadingList(false));
  }, [year]);

  const loadDetail = useCallback((id) => {
    const requestId = ++detailRequestId.current;
    if (!id) { setDetail(null); return Promise.resolve(); }
    return fetchSubstitutionEmployee(id, year)
      .then((data) => {
        if (requestId !== detailRequestId.current) return;
        setDetail(data);
        setDetailError(null);
        const next = nextAbsence(data.absences, today);
        setPresetDates(next ? { start: next.start_date, end: next.end_date } : { start: '', end: '' });
      })
      .catch((err) => {
        if (requestId !== detailRequestId.current) return;
        setDetail(null);
        setDetailError(getApiErrorMessage(err, 'Не удалось загрузить назначения'));
      });
  }, [today, year]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    setOpenForm(null); setFormError(null); setBulkResult(null); setBulkError(null); setBulkSubstitute(''); setBulkComment('');
    setPanelError(null); setShowPastAbsences(false); setQuery('');
    setDetail(null); setDetailError(null); setPresetDates({ start: '', end: '' });
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const reload = () => Promise.all([loadList(), loadDetail(selectedId)]);

  const selectEmployee = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('employee', String(id)); else next.delete('employee');
    setSearchParams(next);
  };

  const { upcoming, rest } = useMemo(() => splitEmployees(employees, today), [employees, today]);
  const candidates = useMemo(
    () => employees.filter((e) => e.id !== selectedId).sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru')),
    [employees, selectedId]
  );
  const bulkWarning = substituteWarning(employees, bulkSubstitute, presetDates.start, presetDates.end);
  const canBulk = Boolean(bulkSubstitute && presetDates.start && presetDates.end) && !bulkBusy;
  const summary = detail ? coverageSummary(detail.rows, presetDates.start, presetDates.end) : null;
  const hidden = detail ? countHiddenPast(detail.rows) : 0;
  const filteredRows = detail
    ? detail.rows.filter((row) =>
        row.client_name.toLowerCase().includes(query.trim().toLowerCase())
      )
    : [];
  const pastAbsenceCount = detail
    ? detail.absences.filter((a) => absenceTileState(a, today) === 'past').length
    : 0;

  const handleBulk = async () => {
    setBulkBusy(true); setBulkError(null); setBulkResult(null);
    try {
      const res = await bulkCreateSubstitutions({
        absent_employee_id: selectedId,
        substitute_employee_id: Number(bulkSubstitute),
        start_date: presetDates.start,
        end_date: presetDates.end,
        comment: bulkComment,
      });
      setBulkResult(
        res.skipped.length === 0
          ? `Создано ${res.created.length}`
          : `Создано ${res.created.length}, пропущено ${res.skipped.length} (уже закрыты)`
      );
      setPanelError(null);
      setBulkComment('');
      await reload();
    } catch (err) {
      setBulkError(getApiErrorMessage(err, 'Не удалось назначить замены'));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleRowSubmit = async (row, values) => {
    setFormBusy(true); setFormError(null);
    try {
      if (openForm?.substitutionId) {
        await updateSubstitution(openForm.substitutionId, values);
      } else {
        await createSubstitution({
          client_id: row.client_id,
          role: row.role,
          absent_employee_id: selectedId,
          ...values,
        });
      }
      setOpenForm(null);
      setPanelError(null);
      await reload();
    } catch (err) {
      setFormError(getApiErrorMessage(err, 'Не удалось сохранить замену'));
    } finally {
      setFormBusy(false);
    }
  };

  const handleExport = async () => {
    if (!detail) return;
    setExportBusy(true);
    setPanelError(null);
    try {
      await exportSubstitutionsXlsx(year, detail.id, exportFilename(year, detail.full_name));
    } catch (err) {
      setPanelError(getApiErrorMessage(err, 'Не удалось скачать файл'));
    } finally {
      setExportBusy(false);
    }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`Удалить замену ${sub.substitute_name} ${formatRange(sub.start_date, sub.end_date)}?`)) return;
    try {
      await deleteSubstitution(sub.id);
      setPanelError(null);
      await reload();
    } catch (err) {
      setPanelError(getApiErrorMessage(err, 'Не удалось удалить замену'));
    }
  };

  if (loadingList && employees.length === 0) return <Loading text="Загрузка бухгалтеров..." />;
  if (listError) return <div className="error">{listError}</div>;

  const renderGroup = (title, items) => (
    <>
      <li className="vs-group-title">
        {title}
        <span className="vs-group-title__n">{items.length}</span>
      </li>
      {items.map((emp) => (
        <EmployeeCard key={emp.id} emp={emp} selected={emp.id === selectedId} onSelect={selectEmployee} />
      ))}
    </>
  );

  return (
    <div className="vs-layout">
      <aside className="vs-side">
        <select
          className="vs-side-select"
          aria-label="Бухгалтер"
          value={selectedId || ''}
          onChange={(e) => selectEmployee(Number(e.target.value) || null)}
        >
          <option value="">— выбрать бухгалтера —</option>
          <optgroup label="В отпуске / скоро">
            {upcoming.map((emp) => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
          </optgroup>
          <optgroup label="Остальные">
            {rest.map((emp) => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
          </optgroup>
        </select>
        <ul className="vs-list" aria-label="Бухгалтеры">
          {upcoming.length > 0 && renderGroup('В отпуске / скоро', upcoming)}
          {rest.length > 0 && renderGroup('Остальные', rest)}
        </ul>
      </aside>

      <section className="vs-main">
        {!selectedId && <div className="empty-state">Выберите бухгалтера слева</div>}
        {selectedId && detailError && <div className="error">{detailError}</div>}
        {selectedId && !detail && !detailError && <SubstitutionsTableSkeleton />}
        {selectedId && detail && !detailError && (
          <>
            <div className="vs-head">
              <div className="vs-head__ident-row">
                <div className="vs-head__ident">
                  <Avatar name={detail.full_name} size={44} />
                  <h3 className="vs-head__name">{detail.full_name}</h3>
                  <span className="vs-head__sub">
                    {detail.rows.length} клиентов · {detail.absences.length} отсутствий в {year}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary vs-head__export"
                  disabled={exportBusy}
                  onClick={handleExport}
                >
                  Экспорт: {detail.full_name}
                </button>
              </div>
              {summary !== null && (
                <CoverageBar covered={summary.full} total={summary.total} />
              )}
              <div className="vs-head__tiles">
                {detail.absences.length === 0 && <span className="vs-muted">Отсутствий в календаре нет</span>}
                {detail.absences.map((a) => {
                  const state = absenceTileState(a, today);
                  if (state === 'past' && !showPastAbsences) return null;
                  return (
                    <AbsenceTile
                      key={`${a.kind}-${a.start_date}-${a.end_date}`}
                      absence={a}
                      state={state}
                      selected={presetDates.start === a.start_date && presetDates.end === a.end_date}
                      onSelect={() => {
                        setPresetDates({ start: a.start_date, end: a.end_date });
                        setPresetVersion((v) => v + 1);
                      }}
                    />
                  );
                })}
                {!showPastAbsences && pastAbsenceCount > 0 && (
                  <button
                    type="button"
                    className="vs-tile-more"
                    onClick={() => setShowPastAbsences(true)}
                  >
                    ещё {pastAbsenceCount} прошедших
                  </button>
                )}
              </div>
            </div>

            <div className="vs-bulk data-card" role="group" aria-label="Заменить всех">
              <div className="vs-bulk__head">
                <div className="vs-bulk-title">Заменить всех</div>
                <div className="vs-bulk__sub">Одним действием на все {detail.rows.length} клиентов</div>
              </div>
              <div className="vs-bulk__form">
                <label htmlFor="vs-bulk-sub" className="vs-label">Замещающий</label>
                <SubstituteSelect id="vs-bulk-sub" value={bulkSubstitute} onChange={setBulkSubstitute} candidates={candidates} />
                <label htmlFor="vs-bulk-start" className="vs-label">С</label>
                <input id="vs-bulk-start" type="date" className="form-input vs-date" value={presetDates.start}
                  onChange={(e) => setPresetDates((p) => ({ ...p, start: e.target.value }))} />
                <label htmlFor="vs-bulk-end" className="vs-label">По</label>
                <input id="vs-bulk-end" type="date" className="form-input vs-date" value={presetDates.end}
                  onChange={(e) => setPresetDates((p) => ({ ...p, end: e.target.value }))} />
                <label htmlFor="vs-bulk-comment" className="vs-label">Комментарий</label>
                <input
                  id="vs-bulk-comment"
                  type="text"
                  maxLength={500}
                  placeholder="необязательно"
                  className="form-input vs-comment"
                  value={bulkComment}
                  onChange={(e) => setBulkComment(e.target.value)}
                />
                <button type="button" className="btn btn-primary vs-bulk__cta" disabled={!canBulk} onClick={handleBulk}>Применить</button>
              </div>
              {summary && summary.full === 0 && summary.total > 0 && (
                <div className="vs-hint">Ни один клиент не закрыт — назначьте одного замещающего на всех</div>
              )}
              {bulkWarning && <div className="vs-warning">{bulkWarning}</div>}
              {bulkError && <div className="vs-error">{bulkError}</div>}
              {bulkResult && <div className="vs-result" role="status">{bulkResult}</div>}
            </div>

            <div className="vs-table-tools">
              <input
                type="search"
                className="form-input vs-search"
                placeholder="Поиск по клиенту"
                aria-label="Поиск по клиенту"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="vs-table-tools__right">
                <Switch
                  id="vs-show-past"
                  label="Показать прошедшие"
                  checked={showPast}
                  onChange={setShowPast}
                />
                {!showPast && hidden > 0 && (
                  <span className="vs-hidden-count" title="Прошедшие замены скрыты">
                    {hidden} скрыто
                  </span>
                )}
              </div>
            </div>
            {panelError && <div className="vs-error" role="alert">{panelError}</div>}

            {detail.rows.length === 0 ? (
              <div className="empty-state">Нет назначений на активных клиентов</div>
            ) : filteredRows.length === 0 ? (
              <div className="empty-state">Ничего не найдено</div>
            ) : (
              <div className="vs-table-wrap">
              <table className="vs-table">
                <thead>
                  <tr><th>Клиент</th><th>Замены</th></tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const rowKey = `${row.client_id}:${row.role}`;
                    const coverage = rowCoverage(row.substitutions, presetDates.start, presetDates.end);
                    const visible = row.substitutions.filter((s) => showPast || s.status !== 'past');
                    const formOpen = openForm?.rowKey === rowKey;
                    const editing = formOpen && openForm.substitutionId
                      ? row.substitutions.find((s) => s.id === openForm.substitutionId)
                      : null;
                    const rowTone = coverage === 'none' ? 'vs-row--none' : coverage === 'partial' ? 'vs-row--partial' : '';
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className={rowTone || undefined}>
                          <td>
                            <div className="vs-cell-client">
                              <span className="vs-client-name">{row.client_name}</span>
                              <span className="vs-role">{ROLE_LABELS[row.role] || row.role}</span>
                              <ClientTeam staff={row.staff || []} excludeEmployeeId={detail.id} />
                            </div>
                          </td>
                          <td>
                            <div className="vs-chips">
                              {visible.length === 0 && (coverage === 'none' || coverage === null) && (
                                <span className="vs-state vs-state--none">Не закрыто</span>
                              )}
                              {coverage === 'partial' && (
                                <span className="vs-state vs-state--partial">Частично</span>
                              )}
                              {visible.map((s) => (
                                <SubstitutionChip
                                  key={s.id}
                                  sub={s}
                                  onEdit={() => { setFormError(null); setOpenForm({ rowKey, substitutionId: s.id }); }}
                                  onDelete={() => handleDelete(s)}
                                />
                              ))}
                              {!formOpen && (
                                <button type="button" className="vs-add"
                                  onClick={() => { setFormError(null); setOpenForm({ rowKey, substitutionId: null }); }}>
                                  + замена
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {formOpen && (
                          <SubstitutionFormRow
                            key={`${rowKey}-form-${openForm.substitutionId || 'new'}-${presetVersion}`}
                            colSpan={2}
                            idPrefix={`vs-${row.client_id}-${row.role}`}
                            candidates={candidates}
                            getWarning={(id, s, e) => substituteWarning(employees, id, s, e)}
                            initial={editing || { start_date: presetDates.start, end_date: presetDates.end }}
                            submitLabel="Сохранить"
                            busy={formBusy}
                            error={formError}
                            onSubmit={(values) => handleRowSubmit(row, values)}
                            onCancel={() => { setOpenForm(null); setFormError(null); }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
